import { useCallback } from 'react';

import { useIntl } from 'react-intl';
import {
	createHook,
	createStore,
	type BoundActions,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import type { DocNode } from '@atlaskit/adf-schema';
import type { CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';

import { appendADFContent } from '../../common/utils/adf';

import messages from './messages';

type State = {
	highlightActionsEditorPlaceholder: DocNode | null;
	highlightActionsEditorPlaceholderCreationContextParams: CreationContextParams | null;
};

const state = createStore({
	initialState: {
		highlightActionsEditorPlaceholder: null,
		highlightActionsEditorPlaceholderCreationContextParams: null,
	} as State,
	actions: {
		setHighlightActionsEditorPlaceholder:
			(
				highlightActionsEditorPlaceholder: State['highlightActionsEditorPlaceholder'],
			): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ highlightActionsEditorPlaceholder });
			},
		setHighlightActionsEditorPlaceholderCreationContextParams:
			(
				highlightActionsEditorPlaceholderCreationContextParams: State['highlightActionsEditorPlaceholderCreationContextParams'],
			): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ highlightActionsEditorPlaceholderCreationContextParams });
			},
		clearHighlightActionsEditorPlaceholderCreationContextParams:
			(): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ highlightActionsEditorPlaceholderCreationContextParams: null });
			},
	},
});

// exported for testing
export const useHighlightActionsEditorPlaceholderStore: HookFunction<
	State,
	BoundActions<
		State,
		{
			setHighlightActionsEditorPlaceholder: (
				highlightActionsEditorPlaceholder: State['highlightActionsEditorPlaceholder'],
			) => ({ setState }: StoreActionApi<State>) => void;
			setHighlightActionsEditorPlaceholderCreationContextParams: (
				highlightActionsEditorPlaceholderCreationContextParams: State['highlightActionsEditorPlaceholderCreationContextParams'],
			) => ({ setState }: StoreActionApi<State>) => void;
			clearHighlightActionsEditorPlaceholderCreationContextParams: () => ({
				setState,
			}: StoreActionApi<State>) => void;
		}
	>,
	void
> = createHook(state);

const getHighlightActionPlaceholderData = (prompt: DocNode, placeholderText: string) => {
	const promptContent = appendADFContent(prompt, placeholderText);
	return promptContent;
};

export const useHighlightActionsEditorPlaceholder = (): {
	highlightActionsEditorPlaceholder: DocNode | null;
	highlightActionsEditorPlaceholderCreationContextParams: CreationContextParams | null;
	insertHighlightActionsEditorPlaceholder: (
		prompt: DocNode,
		creationContextParams?: CreationContextParams,
	) => void;
	clearHighlightActionsEditorPlaceholder: () => void;
	setHighlightActionsEditorPlaceholderCreationContextParams: (
		creationContextParams: CreationContextParams | null,
	) => void;
	clearHighlightActionsEditorPlaceholderCreationContextParams: () => void;
} => {
	const [
		{ highlightActionsEditorPlaceholder, highlightActionsEditorPlaceholderCreationContextParams },
		{
			setHighlightActionsEditorPlaceholder,
			setHighlightActionsEditorPlaceholderCreationContextParams,
			clearHighlightActionsEditorPlaceholderCreationContextParams,
		},
	] = useHighlightActionsEditorPlaceholderStore();
	const { formatMessage } = useIntl();

	const insertHighlightActionsEditorPlaceholder = useCallback(
		(prompt: DocNode, creationContextParams?: CreationContextParams): void => {
			const highlightActionsPlaceholderPrompt = formatMessage(
				messages.highlightActionsPlaceholderPrompt,
			);
			const placeholderData = getHighlightActionPlaceholderData(
				prompt,
				highlightActionsPlaceholderPrompt,
			);

			if (creationContextParams !== undefined) {
				setHighlightActionsEditorPlaceholderCreationContextParams(creationContextParams);
			}
			setHighlightActionsEditorPlaceholder(placeholderData);
		},
		[
			setHighlightActionsEditorPlaceholder,
			setHighlightActionsEditorPlaceholderCreationContextParams,
			formatMessage,
		],
	);

	const clearHighlightActionsEditorPlaceholder = useCallback((): void => {
		setHighlightActionsEditorPlaceholder(null);
		setHighlightActionsEditorPlaceholderCreationContextParams(null);
	}, [
		setHighlightActionsEditorPlaceholder,
		setHighlightActionsEditorPlaceholderCreationContextParams,
	]);

	return {
		highlightActionsEditorPlaceholder,
		highlightActionsEditorPlaceholderCreationContextParams,
		insertHighlightActionsEditorPlaceholder,
		clearHighlightActionsEditorPlaceholder,
		setHighlightActionsEditorPlaceholderCreationContextParams,
		clearHighlightActionsEditorPlaceholderCreationContextParams,
	};
};
